import {
  DeleteOutlined,
  InfoCircleOutlined,
  PlusCircleOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, TextEditController } from '@organisms';
import {
  Button,
  Col,
  Collapse,
  Divider,
  Form,
  Row,
  Switch,
  Typography,
} from 'antd';
import { UploadChangeParam, UploadFile } from 'antd/lib/upload';
import { useCallback, useEffect, useState } from 'react';
import {
  Controller as ControllerHookForm,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import * as yup from 'yup';

import { CancelationButtonTitleFormFields } from 'src/features/Funnel/Components/formFields/CancelationButtonTitleFormFields';
import { LayoutFunnelPage } from 'src/features/Funnel/Components/LayoutFunnelPage';
import { TitleFunnelPage } from 'src/features/Funnel/Components/TitleFunnelPage';
import { UploadImage } from 'src/features/Funnel/Components/UploadImage';
import { useRequestFunctionalityPage } from 'src/features/Funnel/hooks/functionality/useRequestFunctionalityPage';
import { useTryToRequestCreatingFunctionalityFunnelPage } from 'src/features/Funnel/hooks/functionality/useTryToRequestCreatingFunctionalityFunnelPage';

const slidesValidationSchema = yup.object({
  title: yup.string().required('O Titulo Principal é obrigatório'),
  slideTitle: yup.string().required('O Título do slider é obrigatório'),
  web: yup
    .mixed<string | UploadChangeParam<UploadFile>>()
    .required('A Versão Web é obrigatório')
    .test('is-filled', 'A Versão Web é obrigatória', function (value) {
      return value !== null && value !== '';
    }),
  mobile: yup
    .mixed<string | UploadChangeParam<UploadFile>>()
    .required('A Versão mobile é obrigatório')
    .test('is-filled', 'A Versão mobile é obrigatória', function (value) {
      return value !== null && value !== '';
    }),
  description: yup.string().required('A Descrição é obrigatório'),
  enableCta: yup.boolean().required(),
  buttonTitle: yup
    .string()
    .nullable()
    .when('enableCta', {
      is: true,
      then: schema => schema.required('O Título do Botão é obrigatório'),
      otherwise: schema => schema,
    }),
  buttonLink: yup
    .string()
    .nullable()
    .when('enableCta', {
      is: true,
      then: schema => schema.required('O Link do Botão é obrigatório'),
      otherwise: schema => schema,
    }),
  isTarget: yup.boolean().required(),
});

const validationSchema = yup.object({
  cancellationFunnelId: yup
    .string()
    .required('cancellationFunnelId é obrigatório'),
  slides: yup
    .array()
    .of(slidesValidationSchema)
    .required('Informe ao menos um slide'),
  continueCancelationButtonTitle: yup.string().nullable(),
  keepSubscriptionButtonTitle: yup.string().nullable(),
});

export type FunctionalityPageFormData = yup.InferType<typeof validationSchema>;
export type FunctionalityFormDataWithName = FunctionalityPageFormData & {
  name: string;
};

export const FunnelFunctionalityPageScreen: React.FC = () => {
  const [titlePage, setTitlePage] = useState('Modal de Funcionalidade');
  const [isUpdate, setIsUpdate] = useState(false);

  const navigate = useNavigate();

  const { control, handleSubmit, setValue, watch } =
    useForm<FunctionalityPageFormData>({
      resolver: yupResolver(validationSchema),
      defaultValues: {
        cancellationFunnelId: '',
        slides: [
          {
            title: '',
            slideTitle: '',
            web: '',
            mobile: '',
            description: '',
            enableCta: true,
            buttonTitle: '',
            buttonLink: '',
            isTarget: false,
          },
        ],
        continueCancelationButtonTitle: null,
        keepSubscriptionButtonTitle: null,
      },
    });
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'slides',
  });
  const { id } = useParams();

  const {
    isLoadingCreatingFunctionality,
    tryToRequestCreatingFunctionalityFunnelPage,
  } = useTryToRequestCreatingFunctionalityFunnelPage();
  const { isLoadingFunctionalityUpdate, handleUpdateFunctionalityPage } =
    useRequestFunctionalityPage({
      isUpdate,
      setIsUpdate,
      setTitlePage,
      titlePage,
      setValue: setValue,
      replace: replace,
    });

  const handleNewFeature = useCallback(
    async (e: FunctionalityPageFormData) => {
      if (isUpdate) {
        return handleUpdateFunctionalityPage(e);
      }
      await tryToRequestCreatingFunctionalityFunnelPage({
        name: titlePage,
        ...e,
      } as any);
    },
    [
      isUpdate,
      handleUpdateFunctionalityPage,
      tryToRequestCreatingFunctionalityFunnelPage,
      titlePage,
    ]
  );

  useEffect(() => {
    setValue('cancellationFunnelId', id ?? '');
  }, [id]);

  const [activeSlide, setActiveSlide] = useState([fields[0].id]);
  useEffect(() => {
    setActiveSlide([fields[0].id]);
  }, [fields]);

  return (
    <LayoutFunnelPage>
      <TitleFunnelPage titlePage={titlePage} setTitlePage={setTitlePage} />
      <Row>
        <Form
          layout='vertical'
          requiredMark='optional'
          style={{
            width: '100%',
            gap: '24px',
            display: 'flex',
            flexDirection: 'column',
          }}
          onFinish={handleSubmit(handleNewFeature, err => {
            console.error('Erro ao submeter o formulário');
            console.error(err);
          })}
        >
          <Collapse
            activeKey={activeSlide}
            onChange={e => setActiveSlide(e as any)}
            expandIconPosition='end'
          >
            {fields.map((field, index) => (
              <Collapse.Panel
                header={`Slide ${index + 1}`}
                key={field.id}
                extra={
                  <Button
                    type='text'
                    style={{
                      minWidth: 'auto',
                      padding: '1px 6px',
                    }}
                    onClick={() => remove(index)}
                    disabled={fields.length <= 1}
                  >
                    <DeleteOutlined
                      style={{
                        fontSize: '16px',
                        cursor: 'pointer',
                      }}
                    />
                  </Button>
                }
              >
                <Row gutter={[24, 16]}>
                  <Col span={24}>
                    <Controller
                      control={control}
                      name={`slides[${index}].title`}
                      placeholder='Escreva o principal título da página.'
                      label='Título Principal'
                      defaultValue=''
                      showCount={true}
                      maxLength={250}
                      required
                      autoComplete='off'
                    />
                  </Col>
                  <Col span={24}>
                    <Controller
                      control={control}
                      name={`slides[${index}].slideTitle`}
                      placeholder='Escreva o principal título do slide.'
                      label='Título do slider'
                      defaultValue=''
                      showCount={true}
                      maxLength={250}
                      required
                      autoComplete='off'
                    />
                  </Col>
                  <Col span={12}>
                    <UploadImage
                      tooltip={{
                        title: () => (
                          <Typography.Text>
                            <strong>Versão Web</strong> - 482 x 282 pixels (jpg,
                            jpeg, png)
                          </Typography.Text>
                        ),
                        icon: <InfoCircleOutlined />,
                      }}
                      control={control}
                      name={`slides[${index}].web`}
                      label='Versão Web'
                      required
                    />
                  </Col>
                  <Col span={12}>
                    <UploadImage
                      tooltip={{
                        title: () => (
                          <Typography.Text>
                            <strong>Versão App</strong> - 343 x 124 pixels (jpg,
                            jpeg, png)
                          </Typography.Text>
                        ),
                        icon: <InfoCircleOutlined />,
                      }}
                      control={control}
                      name={`slides[${index}].mobile`}
                      label='Versão Mobile'
                      required
                    />
                  </Col>
                  <Col span={24}>
                    <TextEditController
                      control={control}
                      name={`slides[${index}].description`}
                      defaultValue={''}
                      placeholder='Apresente essa forma de contato.'
                      label='Descrição'
                      required
                    />
                  </Col>
                  <Col span={24}>
                    <ControllerHookForm
                      control={control}
                      name={`slides[${index}].enableCta` as any}
                      render={({ field }) => {
                        return (
                          <Row style={{ gap: '8px' }}>
                            <Switch
                              checked={field.value as boolean}
                              onChange={enable => {
                                setValue(
                                  `slides[${index}].enableCta` as any,
                                  enable
                                );
                                if (!enable) {
                                  setValue(
                                    `slides[${index}].buttonTitle` as any,
                                    null
                                  );
                                  setValue(
                                    `slides[${index}].buttonLink` as any,
                                    null
                                  );
                                  setValue(
                                    `slides[${index}].isTarget` as any,
                                    false
                                  );
                                }
                              }}
                            />
                            <Typography>CTA</Typography>
                          </Row>
                        );
                      }}
                    />
                  </Col>
                  <Col span={12}>
                    <Controller
                      control={control}
                      name={`slides[${index}].buttonTitle`}
                      defaultValue={''}
                      placeholder='Título do botão'
                      autoComplete='off'
                      label='Título do botão'
                      showCount={true}
                      maxLength={250}
                      disabled={!watch(`slides[${index}].enableCta` as any)}
                      required={watch(`slides[${index}].enableCta` as any)}
                    />
                  </Col>
                  <Col span={12}>
                    <Controller
                      control={control}
                      name={`slides[${index}].buttonLink`}
                      defaultValue=''
                      placeholder='Link do botão principal'
                      autoComplete='off'
                      label='Link do botão'
                      showCount={true}
                      maxLength={250}
                      disabled={!watch(`slides[${index}].enableCta` as any)}
                      required={watch(`slides[${index}].enableCta` as any)}
                      prefix={
                        <ThunderboltOutlined style={{ color: '#1668DC' }} />
                      }
                    />

                    <ContainerCheckbox>
                      <ControllerHookForm
                        control={control}
                        name={`slides[${index}].isTarget` as any}
                        render={({ field }) => {
                          return (
                            <Row style={{ gap: '8px', alignItems: 'center' }}>
                              <Switch
                                size='small'
                                checked={field.value as boolean}
                                onChange={enable => {
                                  setValue(
                                    `slides[${index}].isTarget` as any,
                                    enable
                                  );
                                }}
                              />
                              <Typography>
                                Abrir em uma página externa
                              </Typography>
                            </Row>
                          );
                        }}
                      />
                    </ContainerCheckbox>
                  </Col>
                </Row>
              </Collapse.Panel>
            ))}
          </Collapse>
          <Row justify='center'>
            <Col>
              <Button
                type='text'
                icon={<PlusCircleOutlined />}
                onClick={() => {
                  append({
                    title: '',
                    slideTitle: '',
                    web: '',
                    mobile: '',
                    description: '',
                    enableCta: true,
                    buttonTitle: '',
                    buttonLink: '',
                    isTarget: false,
                  });
                }}
                disabled={fields.length >= 10}
              >
                Adicionar mais uma opção
              </Button>
            </Col>
          </Row>
          <CancelationButtonTitleFormFields control={control} />

          <Divider orientation='center' style={{ marginTop: '398px' }} />
          <Row justify='end'>
            <Row gutter={[8, 0]}>
              <Col>
                <Button type='default' onClick={() => navigate(-1)}>
                  Cancelar
                </Button>
              </Col>
              <Col>
                <Button
                  type='primary'
                  htmlType='submit'
                  loading={
                    isUpdate
                      ? isLoadingFunctionalityUpdate
                      : isLoadingCreatingFunctionality
                  }
                >
                  Salvar
                </Button>
              </Col>
            </Row>
          </Row>
        </Form>
      </Row>
    </LayoutFunnelPage>
  );
};

const ContainerCheckbox = styled.div`
  position: absolute;

  top: 0;
  right: 5px;
  .ant-form-item {
    margin: 0;
  }
  .ant-form-item-control-input {
    min-height: 0;
  }
`;
